// src/store/slices/ticketSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ticketApi from "api/ticketApi";

export const fetchTickets = createAsyncThunk(
  "ticket/fetchAll",
  async ({ page = 1, pageSize = 10, search = "" }, { rejectWithValue }) => {
    try {
      const res = await ticketApi.getAll(page, pageSize, search);
      return res;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.detail || "Failed to load tickets"
      );
    }
  }
);

export const createTicket = createAsyncThunk(
  "ticket/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await ticketApi.create(data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || "Failed to create ticket");
    }
  }
);

export const updateTicket = createAsyncThunk(
  "ticket/update",
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await ticketApi.update(id, data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || "Failed to update ticket");
    }
  }
);

export const deleteTicket = createAsyncThunk(
  "ticket/delete",
  async (id, { rejectWithValue }) => {
    try {
      await ticketApi.delete(id);
      return id;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.detail || "Failed to delete ticket"
      );
    }
  }
);

// Status actions
// Correct way – throw so action.error is populated properly
export const setInProgress = createAsyncThunk(
  "ticket/inProgress",
  async (id) => {
    try {
      await ticketApi.inProgress(id);
      return id;
    } catch (e) {
      const errorMsg =
        e.response?.data?.error || // ← now "error" key
        e.response?.data?.detail ||
        e.response?.data ||
        "Failed to set In Progress";

      // Throw → becomes action.error
      throw new Error(errorMsg);
      // OR: return rejectWithValue({ message: errorMsg })  // alternative
    }
  }
);

export const closeTicket = createAsyncThunk("ticket/close", async (id) => {
  try {
    await ticketApi.close(id);
    return id;
  } catch (e) {
    const msg = e.response?.data?.error || e.response?.data?.detail || "Failed";
    throw new Error(msg);
  }
});

export const reopenTicket = createAsyncThunk("ticket/reopen", async (id) => {
  try {
    await ticketApi.reopen(id);
    return id;
  } catch (e) {
    const msg = e.response?.data?.error || e.response?.data?.detail || "Failed";
    throw new Error(msg);
  }
});

// export const resolveTicket = createAsyncThunk("ticket/resolve", async (id) => {
//   try {
//     await ticketApi.resolve(id);
//     return id;
//   } catch (e) {
//     const msg = e.response?.data?.error || e.response?.data?.detail || "Failed";
//     throw new Error(msg);
//   }
// });

// Update the resolveTicket thunk in ticketSlice.js
export const resolveTicket = createAsyncThunk(
  "ticket/resolve", 
  async ({ id, resolved_remark }, { rejectWithValue }) => {
    try {
      await ticketApi.resolve(id, resolved_remark);
      return id;
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || "Failed to resolve ticket";
      return rejectWithValue(msg);
    }
  }
);

export const acknowledgeTicket = createAsyncThunk(
  "ticket/acknowledge",
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      await ticketApi.acknowledge(id, data);
      return id;
    } catch (e) {
      const errorMsg =
        e.response?.data?.detail ||
        e.response?.data ||
        "Failed to acknowledge ticket";
      return rejectWithValue(errorMsg);
    }
  }
);

// Messages
export const fetchMessages = createAsyncThunk(
  "ticket/fetchMessages",
  async (ticketId) => {
    const res = await ticketApi.getMessages(ticketId);
    return { ticketId, messages: res };
  }
);
export const sendMessage = createAsyncThunk(
  "ticket/sendMessage",
  async ({ ticketId, message }) => {
    const res = await ticketApi.sendMessage(ticketId, message);
    return { ticketId, message: res };
  }
);
export const editMessage = createAsyncThunk(
  "ticket/editMessage",
  async ({ ticketId, messageId, message }) => {
    const res = await ticketApi.updateMessage(ticketId, messageId, message);
    return { ticketId, message: res };
  }
);
export const removeMessage = createAsyncThunk(
  "ticket/removeMessage",
  async ({ ticketId, messageId }) => {
    await ticketApi.deleteMessage(ticketId, messageId);
    return { ticketId, messageId };
  }
);

// CATEGORIES
export const fetchCategories = createAsyncThunk(
  "ticket/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const data = await ticketApi.getCategories();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load categories"
      );
    }
  }
);

// SUBCATEGORY THUNKS
export const fetchITSubcategories = createAsyncThunk(
  "ticket/fetchITSubcategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await ticketApi.getITSubcategories(categoryId);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load IT subcategories"
      );
    }
  }
);

export const fetchNonITSubcategories = createAsyncThunk(
  "ticket/fetchNonITSubcategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await ticketApi.getNonITSubcategories(categoryId);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load Non-IT subcategories"
      );
    }
  }
);
const getTicketFromPayload = (payload) => {
  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Given a payload from DRF, returns the ticket object.
   * DRF can return the ticket in three forms:
   * 1. raw ticket object
   * 2. { data: ticket }
   * 3. { data: { ... }, pagination: ... } → never for create/update
   * @param {any} payload - DRF response payload
   * @returns {any} - ticket object
   */
  /*******  7dd2a045-38dc-4da4-9b6c-b77adcfb8979  *******/ // DRF can return:
  // 1. raw ticket object
  // 2. { data: ticket }
  // 3. { data: { ... }, pagination: ... } → never for create/update
  return payload?.data || payload;
};

const ticketSlice = createSlice({
  name: "ticket",
  initialState: {
    tickets: [],
    messages: {}, // { [ticketId]: [...] }
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
    // CATEGORIES STATE
    categories: [], // NEW
    categoriesLoading: false, // NEW
    // SUBCATEGORIES STATE
    itSubcategories: [],
    nonItSubcategories: [],
    subcategoriesLoading: false,
    pendingAcknowledgement: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // ← ADD THESE
    // setRealtimeTickets: (state, action) => {
    //   console.log("sate", state, action)
    //   state.tickets = action.payload;
    // },
    // setRealtimeTicket: (state, action) => {
    //   const payload = action.payload;
    //   const idx = state.tickets.findIndex((t) => t.id === payload.id);
    //   if (idx === -1) {
    //     state.tickets.unshift(payload);
    //     state.pagination.count += 1;
    //   } else {
    //     state.tickets[idx] = payload;
    //   }
    // },

    // In ticketSlice.js
    // setRealtimeTickets: (state, action) => {
    //   const ticket = action.payload;
    //   const idx = state.tickets.findIndex((t) => t.id === ticket.id);

    //   if (idx === -1) {
    //     // New ticket → add to top
    //     state.tickets.unshift(ticket);
    //     state.pagination.count += 1;
    //   } else {
    //     // Update existing
    //     state.tickets[idx] = ticket;
    //   }
    // },
    // In ticketSlice.js
    // setRealtimeTickets: (state, action) => {
    //   const payload = action.payload;

    //   if (Array.isArray(payload)) {
    //     // Initial load
    //     state.tickets = payload;
    //     state.pagination.count = payload.length;
    //   } else {
    //     // Single update
    //     const ticket = payload;
    //     const idx = state.tickets.findIndex(t => t.id === ticket.id);
    //     if (idx === -1) {
    //       state.tickets.unshift(ticket);
    //       state.pagination.count += 1;
    //     } else {
    //       state.tickets[idx] = { ...state.tickets[idx], ...ticket };
    //     }
    //   }
    // },

    // setRealtimeTickets: (state, action) => {
    //   const payload = action.payload;
    //   console.log("Payload in setRealtimeTickets:", payload);
    //   if (Array.isArray(payload)) {
    //     payload.forEach((incoming) => {
    //       const i = state.tickets.findIndex((t) => t.id === incoming.id);
    //       if (i !== -1) {
    //         state.tickets[i] = { ...state.tickets[i], ...incoming };
    //       }
    //     });
    //   } else if (payload?.id) {
    //     const i = state.tickets.findIndex((t) => t.id === payload.id);
    //     if (i !== -1) {
    //       state.tickets[i] = { ...state.tickets[i], ...payload };
    //     }
    //   }
    // },


    setRealtimeTickets: (state, action) => {
  const payload = action.payload;

  const handleTicket = (incoming) => {
    if (!incoming?.id) return;

    const existingIndex = state.tickets.findIndex((t) => t.id === incoming.id);

    if (existingIndex !== -1) {
      // Update existing
      state.tickets[existingIndex] = { ...state.tickets[existingIndex], ...incoming };
    } else {
      // APPEND NEW TICKET
      state.tickets.unshift(incoming); // unshift = newest on top (common in ticketing UIs)
      // Or use push() if you want at the end
      // state.tickets.push(incoming);
    }
  };

  if (Array.isArray(payload)) {
    payload.forEach(handleTicket);
  } else {
    handleTicket(payload);
  }

  // Optional: Sort by created_at descending if needed
  // state.tickets.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
},

    // Real-time message handlers
    addRealtimeMessage: (state, action) => {
      const { ticketId, message } = action.payload;
      if (!state.messages[ticketId]) state.messages[ticketId] = [];
      state.messages[ticketId].push(message);
    },

    updateRealtimeMessage: (state, action) => {
      const { ticketId, message } = action.payload;
      const msgs = state.messages[ticketId];
      if (msgs) {
        const idx = msgs.findIndex((m) => m.id === message.id);
        if (idx !== -1) msgs[idx] = message;
      }
    },

    deleteRealtimeMessage: (state, action) => {
      const { ticketId, messageId } = action.payload;
      const msgs = state.messages[ticketId];
      if (msgs) {
        state.messages[ticketId] = msgs.filter((m) => m.id !== messageId);
      }
    },

    setPendingAcknowledgement: (state, action) => {
      const { ticketId, data } = action.payload;
      state.pendingAcknowledgement = {
        ticketId,
        ticket: state.tickets.find((t) => t.id === ticketId) || data,
        open: true,
      };
    },
    clearPendingAcknowledgement: (state) => {
      state.pendingAcknowledgement = null;
    },
  },
  extraReducers: (builder) => {
    // ALL .addCase() FIRST — NO .addMatcher() BEFORE THIS
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tickets = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createTicket.fulfilled, (state, action) => {
        state.tickets.unshift(action.payload);
        state.pagination.count += 1;
      })
      // .addCase(updateTicket.fulfilled, (state, action) => {
      //   const idx = state.tickets?.findIndex((t) => t?.id === action?.payload?.id);
      //   if (idx !== -1) state.tickets[idx] = action?.payload;
      // })
      .addCase(updateTicket.fulfilled, (state, action) => {
        const ticket = getTicketFromPayload(action.payload);

        if (!Array.isArray(state.tickets)) {
          // corrupted state → reset with the updated ticket
          state.tickets = [ticket];
          return;
        }

        const idx = state.tickets.findIndex((t) => t.id === ticket.id);
        if (idx !== -1) {
          state.tickets[idx] = ticket;
        } else {
          // sometimes update returns a ticket that wasn't in the current page
          state.tickets.unshift(ticket);
        }
      })
      .addCase(deleteTicket.fulfilled, (state, action) => {
        state.tickets = state.tickets.filter((t) => t.id !== action.payload);
        state.pagination.count -= 1;
      })

      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messages[action.payload.ticketId] = action.payload.messages;
      })
      // .addCase(sendMessage.fulfilled, (state, action) => {
      //   if (!state.messages[action.payload.ticketId])
      //     state.messages[action.payload.ticketId] = [];
      //   state.messages[action.payload.ticketId].push(action.payload.message);
      // })
      .addCase(editMessage.fulfilled, (state, action) => {
        const msgs = state.messages[action.payload.ticketId];
        if (msgs) {
          const idx = msgs.findIndex((m) => m.id === action.payload.message.id);
          if (idx !== -1) msgs[idx] = action.payload.message;
        }
      })
      .addCase(removeMessage.fulfilled, (state, action) => {
        const msgs = state.messages[action.payload.ticketId];
        if (msgs) {
          state.messages[action.payload.ticketId] = msgs.filter(
            (m) => m.id !== action.payload.messageId
          );
        }
      })
      
      // CATEGORIES
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.error = action.payload;
      })

      // IT Subcategories
      .addCase(fetchITSubcategories.pending, (state) => {
        state.subcategoriesLoading = true;
      })
      .addCase(fetchITSubcategories.fulfilled, (state, action) => {
        state.subcategoriesLoading = false;
        state.itSubcategories = action.payload;
      })
      .addCase(fetchITSubcategories.rejected, (state, action) => {
        state.subcategoriesLoading = false;
        state.error = action.payload;
      })

      // Non-IT Subcategories
      .addCase(fetchNonITSubcategories.pending, (state) => {
        state.subcategoriesLoading = true;
      })
      .addCase(fetchNonITSubcategories.fulfilled, (state, action) => {
        state.subcategoriesLoading = false;
        state.nonItSubcategories = action.payload;
      })
      .addCase(fetchNonITSubcategories.rejected, (state, action) => {
        state.subcategoriesLoading = false;
        // ListItem;
        state.error = action.payload;
      });

    // NOW — AFTER ALL .addCase() — WE CAN SAFELY USE .addMatcher()
    builder.addMatcher(
      (action) =>
        [
          setInProgress,
          closeTicket,
          reopenTicket,
          resolveTicket,
          acknowledgeTicket,
        ].some((thunk) => thunk.fulfilled.match(action)),
      (state, action) => {
        const ticket = state.tickets.find((t) => t.id === action.payload);
        if (!ticket) return;

        if (action.type.includes("close")) ticket.status = "closed";
        else if (action.type.includes("resolve")) ticket.status = "resolved";
        else if (action.type.includes("reopen")) ticket.status = "reopened";
        else if (action.type.includes("inProgress"))
          ticket.status = "in_progress";
        // acknowledge doesn't change visible status
      }
    );
  },
});

export const {
  clearError,
  setRealtimeTickets,
  setRealtimeTicket,
  addRealtimeMessage,
  updateRealtimeMessage,
  deleteRealtimeMessage,
  setPendingAcknowledgement,
  clearPendingAcknowledgement,
} = ticketSlice.actions;
export default ticketSlice.reducer;
